import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Locale } from '@headless-workspace/config';
import { useProductDetailContext } from '@headless-workspace/core-ui';
import { DateHelpers } from '@headless-workspace/utils';
import { getStoresServerAction, LocalityDetails, StoreInfoValue } from '../../../../server';

type UseStoreInfosArgs = {
    locale: Locale;
    getCurrentPosition: () => void;
    localityDetails?: LocalityDetails | null;
    userPosition?: GeolocationPosition | null;
};

type status = 'init' | 'loading' | 'error' | 'success';

export const useStoreInfos = ({ locale, getCurrentPosition, localityDetails, userPosition }: UseStoreInfosArgs) => {
    const [stores, setStores] = useState<StoreInfoValue[]>([]);
    const [currentTime, setCurrentTime] = useState<string | undefined>();
    const [status, setStatus] = useState<status>('init');
    const { sku } = useProductDetailContext();
    const t = useTranslations('Modal.stores');

    const fetchStores = useCallback(
        (latitude: number, longitude: number) => {
            setStatus('loading');
            getStoresServerAction([sku], latitude, longitude).then((result) => {
                if (result.type === 'success') {
                    setStores(result.data);
                    setStatus('success');

                    const date = new Date();
                    setCurrentTime(DateHelpers.formatDateTimeWithHourMinute(locale, t('list.label.at'), date));
                } else if (result.type === 'failure') {
                    setStatus('error');
                    // TODO: Better error handling with toasts
                    console.error('Error fetching stores', result.meta);
                }
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [locale, sku],
    );

    // Fetch stores on load with user position
    useEffect(() => {
        if (userPosition) {
            fetchStores(userPosition.coords.latitude, userPosition.coords.longitude);
        } else {
            getCurrentPosition();
        }
    }, [fetchStores, userPosition, getCurrentPosition]);

    // Fetch stores when locality details are available (after locality click)
    useEffect(() => {
        localityDetails &&
            fetchStores(localityDetails.geometry.location.latitude, localityDetails.geometry.location.longitude);
    }, [fetchStores, localityDetails]);

    return {
        stores,
        currentTime,
        status,
    };
};
